import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';
import { Button } from '@/ui/ui/button';
import { Avatar, AvatarFallback } from '@/ui/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/ui/tooltip';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '@/ui/ui/sheet';
import {
  Trophy,
  LayoutDashboard,
  Calendar,
  Building2,
  Users,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  Dumbbell,
  Clock,
  Activity,
  CreditCard,
  Code2,
  ChevronRight,
  Palette,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/ui/notifications/NotificationBell';
import { ShellThemeToggle } from '@/ui/layout/ShellThemeToggle';
import { AppBuildBadge } from '@/ui/layout/AppBuildBadge';
import { ExperienceModePanel } from '@/ui/layout/ExperienceModePanel';
import { type ExperienceMode, useExperienceMode } from '@/hooks/useExperienceMode';
import {
  createExperienceModePolicy,
  type ExperienceModeNavItemId,
} from '@/hooks/experienceModePolicy';
import { getDefaultLandingPathForMode } from '@/modules/auth/core/postAuthLanding';
import { businessReadService } from '@/modules/business';
import { unwrapOrThrow } from '@/modules/shared/core/result';
import {
  clearRecoveryPromptPending,
  consumeRecoveryPromptPending,
} from '@/modules/auth/infrastructure/recoveryFlowStorage';
import { Badge } from '@/ui/ui/badge';
import { useNotifications } from '@/hooks/useNotifications';
import {
  REGISTER_BUSINESS_FRESH_ENTRY_STATE,
  REGISTER_BUSINESS_PATH,
} from '@/modules/workflow-entry/core/registerBusinessRoute';

interface NavItem {
  id: ExperienceModeNavItemId;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  href?: string;
  children?: NavChildItem[];
}

interface NavSection {
  id: string;
  title: string;
  itemIds: ExperienceModeNavItemId[];
}

interface NavChildItem {
  id?: string;
  label: string;
  href?: string;
  state?: unknown;
  icon?: React.ComponentType<{ className?: string }>;
  children?: NavChildItem[];
}

// Keep the shell grouped by business-facing navigation areas so the selector can grow later without reshaping the layout.
const NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    itemIds: ['dashboard', 'book', 'my_bookings'],
  },
  {
    id: 'business-management',
    title: 'Business Management',
    itemIds: ['manage_businesses'],
  },
  {
    id: 'business-workspace',
    title: 'Business Workspace',
    itemIds: ['my_business', 'sports_courts', 'users', 'bookings', 'activity_logs'],
  },
  {
    id: 'platform-admin',
    title: 'Platform Admin',
    itemIds: ['businesses', 'pricing'],
  },
  {
    id: 'development',
    title: 'Development',
    itemIds: ['dev'],
  },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const AccountMenu: React.FC<{
  initials: string;
  fullName: string;
  email: string;
  onSettings: () => void;
  onLogout: () => void;
}> = ({ initials, fullName, email, onSettings, onLogout }) => {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full min-w-0 justify-start gap-3 rounded-xl border border-sidebar-border/70 bg-sidebar-accent/20 px-3 py-2.5 text-left hover:bg-sidebar-accent/40"
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 text-left leading-tight">
                <p className="truncate text-sm font-semibold text-sidebar-foreground">{fullName}</p>
                <p className="truncate text-xs text-sidebar-foreground/60">{email}</p>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-sidebar-foreground/60" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-64">
          <div className="space-y-0.5">
            <p className="text-sm font-semibold">{fullName}</p>
            <p className="text-xs text-muted-foreground">{email}</p>
          </div>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" side="top" className="w-56">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSettings}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, profile, signOut, isSuperAdmin, isBusinessAdmin, isStaff, getBusinessIds, getFacilityIds } = useAuth();
  const { settings: platformSettings } = usePlatformSettings();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(() => consumeRecoveryPromptPending());
  const [openNavGroups, setOpenNavGroups] = useState<Record<string, boolean>>({});
  const showDevMenu = import.meta.env.VITE_APP_ENV === 'dev';
  const { availableModes, selectedMode, setSelectedMode } = useExperienceMode({
    isSuperAdmin,
    isBusinessAdmin,
    isStaff,
  });
  const isFacilityScopedAdmin = isBusinessAdmin || isStaff;
  const adminFacilityIds = Array.from(new Set([...getBusinessIds('business_admin'), ...getFacilityIds('staff')]));
  const { data: adminFacilities = [] } = useQuery({
    queryKey: ['shell-book-nav-facilities', isSuperAdmin, adminFacilityIds],
    queryFn: async () => {
      if (!isFacilityScopedAdmin && !isSuperAdmin) return [];

      // Scope the booking CTA to the facilities this admin can actually manage.
      // Public discovery still uses the unscoped active-facility query.
      return unwrapOrThrow(
        await businessReadService.getActiveFacilities({
          isSuperAdmin,
          facilityIds: adminFacilityIds,
        }),
      );
    },
    enabled: isFacilityScopedAdmin || isSuperAdmin,
  });
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const shellHomePath = getDefaultLandingPathForMode(selectedMode);
  useEffect(() => {
    if (adminFacilities.length === 0) {
      if (selectedFacilityId !== '') {
        setSelectedFacilityId('');
      }
      return;
    }

    // Only auto-pick a facility when the shell has exactly one valid choice.
    // When multiple facilities exist, keep the selector explicit so the booking link does not guess.
    if (adminFacilities.length === 1) {
      if (selectedFacilityId !== adminFacilities[0].id) {
        setSelectedFacilityId(adminFacilities[0].id);
      }
      return;
    }

    const hasSelectedFacility = adminFacilities.some((facility) => facility.id === selectedFacilityId);
    if (!hasSelectedFacility && selectedFacilityId !== '') {
      setSelectedFacilityId('');
    }
  }, [adminFacilities, selectedFacilityId]);

  const selectedFacility = useMemo(
    () => adminFacilities.find((facility) => facility.id === selectedFacilityId) ?? null,
    [adminFacilities, selectedFacilityId],
  );
  const experienceModePolicy = React.useMemo(
    () =>
      createExperienceModePolicy({
        selectedMode,
        isSuperAdmin,
        isBusinessAdmin,
        isStaff,
        showDevMenu,
      }),
    [isBusinessAdmin, isStaff, isSuperAdmin, selectedMode, showDevMenu],
  );
  const bookHref = React.useMemo(() => {
    if (selectedMode !== 'business_admin' && selectedMode !== 'staff') {
      return '/book';
    }

    return selectedFacility?.slug ? `/book/${selectedFacility.slug}` : '/book';
  }, [selectedFacility, selectedMode]);

  const navItemsById = React.useMemo<Record<ExperienceModeNavItemId, NavItem>>(
    () => ({
      dashboard: { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      book: { id: 'book', label: 'Book a Court', href: bookHref, icon: Calendar },
      my_bookings: { id: 'my_bookings', label: 'My Bookings', href: '/bookings', icon: Clock },
      // Keep the navigation label aligned with the business-facing route.
      manage_businesses: { id: 'manage_businesses', label: 'Manage Businesses', href: '/manage-businesses', icon: Building2 },
      // These links are the actual admin surfaces behind the business wording.
      businesses: { id: 'businesses', label: 'Businesses', href: '/admin/businesses', icon: Building2 },
      my_business: { id: 'my_business', label: 'My Business', href: '/admin/business', icon: Building2 },
      sports_courts: { id: 'sports_courts', label: 'Sports & Courts', href: '/admin/sports', icon: Dumbbell },
      users: { id: 'users', label: 'Users', href: '/admin/users', icon: Users },
      bookings: { id: 'bookings', label: 'Bookings', href: '/admin/bookings', icon: Calendar },
      activity_logs: { id: 'activity_logs', label: 'Activity Logs', href: '/admin/activity', icon: Activity },
      pricing: {
        id: 'pricing',
        label: 'Pricing',
        icon: CreditCard,
        children: [
          { label: 'Plans & Pricing', href: '/pricing' },
          { label: 'Manage Pricing', href: '/admin/pricing' },
        ],
      },
      dev: {
        id: 'dev',
        label: 'Dev',
        icon: Code2,
        children: [
          {
            label: 'Workflow / Commerce',
            icon: CreditCard,
            children: [
              {
                label: 'Onboarding & Auth',
                icon: Building2,
                children: [
                  { label: 'Register Business', href: REGISTER_BUSINESS_PATH, state: REGISTER_BUSINESS_FRESH_ENTRY_STATE },
                  { label: 'Auth Callback', href: '/auth/callback' },
                  { label: 'Verify Email Pending', href: '/verify-email-pending' },
                  { label: 'Dashboard', href: '/dashboard' },
                ],
              },
              {
                label: 'Pricing & Billing',
                icon: CreditCard,
                children: [
                  { label: 'Pricing', href: '/pricing' },
                  { label: 'Manage Pricing', href: '/admin/pricing' },
                ],
              },
              {
                label: 'Admin Surfaces',
                icon: LayoutDashboard,
                children: [
                  { label: 'My Business', href: '/admin/business' },
                  { label: 'All Businesses', href: '/admin/businesses' },
                  { label: 'Sports', href: '/admin/sports' },
                  { label: 'Users', href: '/admin/users' },
                  { label: 'Bookings', href: '/admin/bookings' },
                  { label: 'Activity Logs', href: '/admin/activity' },
                ],
              },
            ],
          },
          {
            label: 'UI / UX Theme',
            icon: Palette,
            children: [
              { label: 'Theme Hub', href: '/dev/theme' },
              { label: 'Theme Overview', href: '/dev/theme/overview' },
              { label: 'Theme Forms', href: '/dev/theme/forms' },
              { label: 'Theme Navigation', href: '/dev/theme/navigation' },
              { label: 'UI Playground', href: '/dev/theme/playground' },
            ],
          },
          { label: 'Build Changelog', href: '/dev/changelog' },
          { label: 'Domain Glossary', href: '/dev/glossary' },
          { label: 'Workflow Trace', href: '/dev/workflow-trace' },
          { label: 'Review prototype routes', href: '/book/prototypes' },
          { label: 'Landing Prototypes', href: '/landing/prototypes' },
        ],
      },
    }),
    [bookHref],
  );

  const PlatformLogo = ({ className }: { className?: string }) => (
    platformSettings.platform_icon_url ? (
      <img src={platformSettings.platform_icon_url} alt={platformSettings.platform_name} className={className} />
    ) : (
      <Trophy className={className} />
    )
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleModeChange = React.useCallback(
    (mode: ExperienceMode) => {
      setSelectedMode(mode);
      setMobileOpen(false);
      navigate(getDefaultLandingPathForMode(mode));
    },
    [navigate, setSelectedMode],
  );

  const getNavKey = (item: NavChildItem, parentKey?: string) => {
    const itemKey = item.id ?? item.href ?? item.label;
    return parentKey ? `${parentKey}/${itemKey}` : itemKey;
  };

  const hasActiveDescendant = (item: NavChildItem): boolean => {
    if (item.href && location.pathname === item.href) {
      return true;
    }

    return item.children?.some((child) => hasActiveDescendant(child)) ?? false;
  };

  const renderNavItem = (item: NavChildItem, parentKey = '', depth = 0): React.ReactNode => {
    const itemKey = getNavKey(item, parentKey);
    const hasChildren = (item.children?.length ?? 0) > 0;
    const isActive = item.href ? location.pathname === item.href : false;
    const hasActiveChild = hasChildren ? hasActiveDescendant(item) : false;
    // Keep the Dev menu collapsed by default so diagnostic routes do not crowd the shell.
    const isGroupOpen = item.id === 'dev' ? openNavGroups[itemKey] ?? false : openNavGroups[itemKey] ?? hasActiveChild;

    if (hasChildren) {
      return (
        <div
          key={itemKey}
          className={cn('space-y-1', depth > 0 && 'ml-3 border-l border-sidebar-border pl-3')}
          data-nav-item-id={item.id ?? item.href ?? item.label}
        >
          <button
            type="button"
            aria-expanded={isGroupOpen}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              hasActiveChild
                ? 'bg-sidebar-accent/70 text-sidebar-accent-foreground'
                : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50',
            )}
            onClick={() =>
              setOpenNavGroups((current) => ({
                ...current,
                [itemKey]: !isGroupOpen,
              }))
            }
          >
            {item.icon ? <item.icon className="h-5 w-5 shrink-0" /> : <span className="h-5 w-5 shrink-0" />}
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronRight className={cn('h-4 w-4 shrink-0 transition-transform', isGroupOpen && 'rotate-90')} />
          </button>
          {isGroupOpen && (
            <div className="flex flex-col gap-1 pl-6">
              {item.children?.map((child) => renderNavItem(child, itemKey, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return item.href ? (
      <Link
        key={itemKey}
        to={item.href}
        state={item.state}
        data-nav-item-id={item.id ?? item.href ?? item.label}
        onClick={() => setMobileOpen(false)}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          depth > 0 && 'ml-3 border-l border-transparent pl-3',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
        )}
      >
        {item.icon ? <item.icon className="h-5 w-5 shrink-0" /> : <span className="h-5 w-5 shrink-0" />}
        {item.label}
      </Link>
    ) : null;
  };

  const renderNavSection = (section: NavSection) => {
    const items = section.itemIds
      .filter((itemId) => experienceModePolicy.isNavItemVisible(itemId))
      .map((itemId) => navItemsById[itemId]);

    if (items.length === 0) {
      return null;
    }

    return (
      <React.Fragment key={section.id}>
        <div className="px-3 pt-4 pb-2 text-xs font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/50">
          {section.title}
        </div>
        <div className="flex flex-col gap-1">
          {items.map((item) => renderNavItem(item))}
        </div>
      </React.Fragment>
    );
  };

  const NavLinks = () => (
    <nav aria-label="Primary navigation" className="flex flex-col gap-1">
      {NAV_SECTIONS.map((section) => renderNavSection(section))}
    </nav>
  );

  const userInitials = profile?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 bg-sidebar border-r border-sidebar-border">
        <Link to={shellHomePath} className="flex h-16 items-center gap-2 px-6 border-b border-sidebar-border shrink-0">
          <PlatformLogo className="h-7 w-7 text-sidebar-primary" />
          <span className="text-xl font-bold text-sidebar-foreground">{platformSettings.platform_name}</span>
        </Link>
        <div className="flex-1 overflow-auto p-4">
          <NavLinks />
        </div>
        <div className="p-4 border-t border-sidebar-border shrink-0 space-y-3">
          {(isSuperAdmin || isBusinessAdmin || isStaff) && (
            <ExperienceModePanel
              facilityOptions={adminFacilities.map((facility) => ({
                facilityId: facility.id,
                facilityName: facility.name,
                facilitySlug: facility.slug ?? null,
              }))}
              selectedFacilityId={selectedFacilityId}
              onFacilityChange={setSelectedFacilityId}
              availableModes={availableModes}
              selectedMode={selectedMode}
              onModeChange={handleModeChange}
              />
            )}
          <div className="flex items-center justify-center gap-2">
            <NotificationBell />
            <ShellThemeToggle />
          </div>
          <div className="flex justify-center">
            <AppBuildBadge />
          </div>
          <AccountMenu
            initials={userInitials}
            fullName={profile?.full_name || 'User'}
            email={user?.email || 'No email'}
            onSettings={() => navigate('/settings')}
            onLogout={handleSignOut}
          />
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex min-h-0 flex-1 flex-col lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden flex h-16 items-center justify-between border-b border-border/80 bg-background/95 px-4 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <Link to={shellHomePath} className="flex items-center gap-2">
            <PlatformLogo className="h-6 w-6 text-primary" />
            <span className="text-lg font-bold">{platformSettings.platform_name}</span>
          </Link>
          <div className="flex items-center">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" aria-label="Open navigation menu">
                  <Menu className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center p-0 text-xs border-2 border-background shadow-sm"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 bg-sidebar p-0 flex h-full flex-col overflow-hidden">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Browse navigation, notifications, and theme controls.
                </SheetDescription>
                <Link to={shellHomePath} onClick={() => setMobileOpen(false)} className="flex h-16 items-center gap-2 border-b border-sidebar-border/80 px-6 shadow-sm shrink-0">
                  <PlatformLogo className="h-7 w-7 text-sidebar-primary" />
                  <span className="text-xl font-bold text-sidebar-foreground">{platformSettings.platform_name}</span>
                </Link>
                <div className="flex-1 overflow-y-auto p-4">
                  <NavLinks />
                </div>
                <div className="mt-auto shrink-0 border-t border-sidebar-border p-4">
                  <div className="mb-3 flex items-center justify-center gap-2">
                    <NotificationBell />
                    <ShellThemeToggle />
                  </div>
                  <div className="mb-3 flex justify-center">
                    <AppBuildBadge />
                  </div>
                  {(isSuperAdmin || isBusinessAdmin || isStaff) && (
                    <div className="mb-3">
                      <ExperienceModePanel
                        facilityOptions={adminFacilities.map((facility) => ({
                          facilityId: facility.id,
                          facilityName: facility.name,
                          facilitySlug: facility.slug ?? null,
                        }))}
                        selectedFacilityId={selectedFacilityId}
                        onFacilityChange={setSelectedFacilityId}
                        availableModes={availableModes}
                        selectedMode={selectedMode}
                        onModeChange={handleModeChange}
                      />
                    </div>
                  )}
                  <AccountMenu
                    initials={userInitials}
                    fullName={profile?.full_name || 'User'}
                    email={user?.email || 'No email'}
                    onSettings={() => { setMobileOpen(false); navigate('/settings'); }}
                    onLogout={() => { setMobileOpen(false); void handleSignOut(); }}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-0 flex-1 overflow-y-auto">
          {showRecoveryPrompt && (
            <div className="border-b border-primary/20 bg-primary/5 px-4 py-3 sm:px-6">
              <div className="flex flex-col gap-3 rounded-lg bg-background/90 p-4 shadow-sm ring-1 ring-primary/10 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">You&apos;re signed in.</p>
                  <p className="text-sm text-muted-foreground">
                    Set a new password now, or continue and change it later in Settings.
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      setShowRecoveryPrompt(false);
                      clearRecoveryPromptPending();
                      navigate('/reset-password', { replace: true });
                    }}
                  >
                    Set new password
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowRecoveryPrompt(false);
                      clearRecoveryPromptPending();
                    }}
                  >
                    Continue for now
                  </Button>
                </div>
              </div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};



